/**
 * "Nueva canción" (CMS-03 → CMS-04). A song *is* a catalog MP4, so creation only asks for the
 * three things `songs` cannot derive — title, credits and `source_id` (`videos/<id>.mp4`,
 * docs/02 §3) — and then opens the full editor on the new draft.
 */
import type { ArtistRef } from '@akpl/shared';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { api } from '../api';
import { ArtistSelect } from '../components/catalog/ArtistSelect';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/button';
import { Field, Input, Select } from '../components/ui/field';
import { Panel, PanelHeader } from '../components/ui/panel';
import { useT } from '../i18n';

export function NewSong() {
  const { t } = useT();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [artists, setArtists] = useState<ArtistRef[]>([]);
  const [sourceId, setSourceId] = useState('');
  const [language, setLanguage] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api.cmsSongCreate({
        body: {
          title: title.trim(),
          artistIds: artists.map((a) => a.id),
          sourceId: Number(sourceId),
          ...(language ? { language: language as 'en' | 'es' | 'pt' } : {}),
        },
      }),
    onSuccess: (res) => navigate({ to: '/songs/$songId', params: { songId: res.song.id } }),
  });

  const valid = title.trim().length > 0 && artists.length > 0 && Number(sourceId) > 0;

  return (
    <>
      <PageHeader
        title={t('cms:songs.new')}
        subtitle={
          <Link to="/songs" className="text-pink hover:underline">
            {t('cms:songs.editor.back')}
          </Link>
        }
      />
      <div className="max-w-2xl px-6 pb-10">
        <Panel>
          <PanelHeader
            title={t('cms:songs.editor.data')}
            subtitle={t('cms:songs.editor.dataHelp')}
          />
          <div className="flex flex-col gap-3 px-4 py-4">
            <Field label={t('cms:songs.fields.title')}>
              {(id) => <Input id={id} value={title} onChange={(e) => setTitle(e.target.value)} />}
            </Field>
            <ArtistSelect value={artists} onChange={setArtists} />
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('cms:songs.fields.sourceId')} help="videos/&lt;source_id&gt;.mp4">
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    min={1}
                    value={sourceId}
                    onChange={(e) => setSourceId(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('cms:songs.filters.language')}>
                {(id) => (
                  <Select id={id} value={language} onChange={(e) => setLanguage(e.target.value)}>
                    <option value="">—</option>
                    {(['es', 'en', 'pt'] as const).map((code) => (
                      <option key={code} value={code}>
                        {t(`cms:songs.languages.${code}` as 'cms:songs.languages.es')}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
            <div className="flex items-center gap-2">
              <Button disabled={!valid || create.isPending} onClick={() => create.mutate()}>
                {t('common:actions.save')}
              </Button>
              {create.isError && (
                <span className="text-[12px] text-danger" role="alert">
                  {t('common:states.error')}
                </span>
              )}
            </div>
          </div>
        </Panel>
      </div>
    </>
  );
}
